import React, { useMemo, useState } from 'react';
import DownIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Menu } from '@mui/material';

import { Text } from '../../../styledComponents';

import '../styles/customSelect.scss';

import { AddCircleOutline } from '@mui/icons-material';

import AvatarComponent from '../../../../components/common-components/AvatarComponent/AvatarComponent';
import CustomTooltip from '../../../../components/common-components/CustomTooltip/CustomTooltip';
import ShortString from '../../../../components/common-components/ShortString/ShortString';
import TickIcon from '../../../../components/Icons/icons/TickIcon';
import XIcon from '../../../Icons/icons/XIcon';

export default function CustomSelect({
  label,
  rightLabelText,
  value,
  onChange,
  options,
  errorText,
  selectListHeader,
  disabled,
  placeholderText = 'Select an option',
  renderOption,
  style,
  selectedStyle = {},
  showCancelIcon = false,
  unselectedIcon = <DownIcon sx={{ width: '22px', height: '22px' }} />,
  dropDownMaxHeight = '160px',
  renderSelectedLabel,
  labelContainerStyle,
  required,
  dropDownContainerStyle = {},
  selectedOptionsStyle = {},
  showProfilePic = false,
  showShortLabel = false,
  optionStyles,
  tickWidth = 20,
  tickHeight = 30,
  shortStringLength = 10,
  showShortOption = false,
  showTooltipOnCancel = false,
  additionalField = false,
  handleAddOption = () => {},
  showTick = true,
}) {
  const [showDropdown, setShowDropdown] = useState(null);
  const [selected, setSelected] = useState(false);

  const selectedOption = useMemo(() => options?.find((op) => op?.value === value), [value, options]);

  const renderSelected = () => {
    if (renderSelectedLabel) return renderSelectedLabel();

    return (
      <div style={{ display: 'flex', justifyContent: 'start', alignItems: 'cennter', gap: '4px' }}>
        {selectedOption?.icon ? <span style={{ marginRight: '8px' }}>{selectedOption.icon}</span> : <></>}
        {showProfilePic && (
          <span>
            <AvatarComponent src={selectedOption?.profile_pic} size={20} alt={selectedOption?.label} />
          </span>
        )}
        {showShortLabel ? (
          <ShortString
            name={selectedOption?.label || selectedOption?.value || placeholderText}
            stringLength={shortStringLength}
            style={selectedOptionsStyle}
          />
        ) : (
          <span
            style={{
              fontSize: '16px',
              color: selectedOption?.label || selectedOption?.value ? 'var(--text-default)' : 'var(--text-disabled)',
              ...selectedOptionsStyle,
            }}>
            {' '}
            {selectedOption?.label || selectedOption?.value || placeholderText}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="customSelect">
      {label && (
        <label style={labelContainerStyle}>
          <span>{label}</span>
          {required && <span style={{ color: 'var(--text-critical)' }}> *</span>}
          <span>{rightLabelText}</span>
        </label>
      )}
      <div
        style={{ border: Boolean(showDropdown) && '1px solid var(--border-highlight)', ...style }}
        className={`inputField ${disabled ? 'inputFieldDisabled' : null} `}
        onClick={(e) => {
          if (disabled) return;
          setShowDropdown(e.currentTarget);
        }}>
        <p style={{ color: 'var(--text-default)', ...selectedStyle }}>{renderSelected()}</p>
        {!selected ? (
          <span style={{ transform: showDropdown && 'rotate(180deg)', transition: 'transform 200ms ease-in-out' }}>
            {unselectedIcon}
          </span>
        ) : showCancelIcon ? (
          <CustomTooltip show={showTooltipOnCancel} title="Remove">
            <div
              onClick={(e) => {
                e.stopPropagation();
                onChange('', '');
                setSelected(false);
              }}>
              <XIcon width={tickWidth} height={tickHeight} style={{ alignSelf: 'center', color: '#E54848' }} />
            </div>
          </CustomTooltip>
        ) : (
          unselectedIcon
        )}
      </div>
      <Menu
        disablePortal
        anchorEl={showDropdown}
        open={Boolean(showDropdown) && options.length > 0}
        onClose={() => setShowDropdown(null)}
        PaperProps={{
          sx: {
            width: showDropdown?.offsetWidth,
            maxHeight: dropDownMaxHeight,
            overflowY: 'scroll',
            borderRadius: '0 0 4px 4px !important',
            ...dropDownContainerStyle,
          },
        }}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}>
        {selectListHeader?.length && <p className="select-list-header">{selectListHeader}</p>}
        {options[0]?.value === -1 ? (
          <div className="center" style={{ height: '248px' }}>
            <EmptyDropDownFtux />
          </div>
        ) : (
          <div>
            {options?.map((option, index) => {
              if (renderOption) {
                return renderOption({
                  option,
                  index,
                  onChange,
                  value,
                  setShowDropdown,
                });
              }
              return (
                <div
                  className="menuItem custom-select-menuItem"
                  style={{
                    backgroundColor: option.value === value ? 'var(--purple-p-10)' : '',
                    color: option.value === value ? 'var(--purple-p-400)' : '',
                    ...optionStyles,
                  }}
                  key={index}
                  onClick={(e) => {
                    onChange(option.value, option.label);
                    setShowDropdown(null);
                    setSelected(true);
                  }}>
                  <div>
                    {option?.icon ? <span style={{ marginRight: '8px' }}>{option.icon}</span> : <></>}
                    {showShortOption ? (
                      <ShortString name={option.label} stringLength={shortStringLength} placement="bottom" />
                    ) : (
                      option.label
                    )}
                  </div>
                  {showTick && option.value === value && (
                    <div>
                      <TickIcon width={tickWidth} height={tickHeight} color="var(--purple-p-400)" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {additionalField && (
          <div className="p-2">
            <div
              className="dropdown-add-option"
              onClick={(e) => {
                handleAddOption(e);
                setShowDropdown(null);
                setSelected(true);
              }}>
              <CustomTooltip title="Custom">
                <AddCircleOutline sx={{ color: 'var(--purple-p-400)', width: '18px' }} />
              </CustomTooltip>
            </div>
          </div>
        )}
      </Menu>
      <Text color="#e64848" fontSize="md">
        {errorText}
      </Text>
    </div>
  );
}

const EmptyDropDownFtux = () => {
  return (
    <div className="empty-dropdown-ftux">
      <div className="box-group">
        <div className="box"></div>
        <div className="box"></div>
        <div className="box"></div>
      </div>
      <p className="message">No filters saved yet!</p>
    </div>
  );
};
